import Decimal from "decimal.js";
import {
  AreaUnitEnum,
  AreaUnitSchema,
  DimensionUnitSchema,
  ItemUnitEnum,
  WeightUnitSchema,
  type AreaUnit,
  type ConversionUnit,
  type DimensionUnit,
  type Item,
  type ItemUnit,
  type ItemUnitConversion,
  type WeightUnit,
} from "../schemas/items";
import type { ItemOverrideField } from "../schemas/quotes";

/** Measurements of one piece of an item, as stored on the item. */
export interface PieceMeasurements {
  dimensionUnit?: DimensionUnit | null;
  length?: string | null;
  width?: string | null;
  weightUnit?: WeightUnit | null;
  weight?: string | null;
}

/** Everything the conversion engine needs to know about an item. */
export interface UnitConversionContext {
  itemUnit: ItemUnit;
  piece: PieceMeasurements;
  rates: readonly ItemUnitConversion[];
}

interface ConversionEdge {
  from: ConversionUnit;
  to: ConversionUnit;
  factor: Decimal;
}

interface PathEnd {
  unit: ConversionUnit;
  factor: Decimal;
}

const FEET_PER_LENGTH_UNIT: Record<DimensionUnit, Decimal> = {
  feet: new Decimal(1),
  inches: new Decimal(1).div(12),
  meters: new Decimal("3.28084"),
  centimeters: new Decimal("0.0328084"),
  MLF: new Decimal(1000),
};

const KG_PER_WEIGHT_UNIT: Record<WeightUnit, Decimal> = {
  kg: new Decimal(1),
  pounds: new Decimal(1).div(new Decimal("2.20462")),
};

const SQUARE_FEET_PER_AREA_UNIT: Record<AreaUnit, Decimal> = {
  square_feet: new Decimal(1),
  square_inches: new Decimal(1).div(144),
  square_meters: new Decimal("10.7639"),
};

const FIXED_RATIO_EDGES: ConversionEdge[] = [
  ...ratioEdges(WeightUnitSchema.options, KG_PER_WEIGHT_UNIT),
  ...ratioEdges(DimensionUnitSchema.options, FEET_PER_LENGTH_UNIT),
  ...ratioEdges(AreaUnitSchema.options, SQUARE_FEET_PER_AREA_UNIT),
];

/** Returns how many `to` units make one `from` unit, e.g. 12 from feet to inches. */
export function dimensionUnitFactor(
  from: DimensionUnit,
  to: DimensionUnit,
): Decimal {
  return FEET_PER_LENGTH_UNIT[from].div(FEET_PER_LENGTH_UNIT[to]);
}

/**
 * Builds the conversion context from an item's unit and measurements. The
 * item's allowance is added to its length and width; an empty size stays empty.
 */
export function buildConversionContext(
  item: Pick<
    Item,
    | "unit"
    | "weight"
    | "weightUnit"
    | "dimensionUnit"
    | "length"
    | "width"
    | "allowance"
    | "unitConversions"
  >,
): UnitConversionContext {
  const allowance = toPositiveDecimal(item.allowance);
  return {
    itemUnit: item.unit,
    piece: {
      dimensionUnit: item.dimensionUnit,
      length: withAllowance(item.length, allowance),
      width: withAllowance(item.width, allowance),
      weightUnit: item.weightUnit,
      weight: item.weight,
    },
    rates: item.unitConversions ?? [],
  };
}

/**
 * Returns the cut dimensions an item priced by weight from its cut size needs.
 * @param item - The item, with its unit conversion rates
 * @returns Width and length for an area-to-pounds rate, length for a feet-to-pounds rate, else none
 */
export function requiredCutDimensions(
  item: Pick<Item, "unitConversions">,
): ("width" | "length")[] {
  const poundsRates = (item.unitConversions ?? []).filter(
    (rate) => rate.toUnit === ItemUnitEnum.pounds,
  );
  if (
    poundsRates.some((rate) => AreaUnitSchema.safeParse(rate.fromUnit).success)
  ) {
    return ["width", "length"];
  }
  if (poundsRates.some((rate) => rate.fromUnit === ItemUnitEnum.feet)) {
    return ["length"];
  }
  return [];
}

/**
 * Returns a copy with "1" for each empty length, width or weight in `fields`
 * whose unit is set, so reachability equals "a quote line could supply it".
 * Never price with it.
 */
export function withLineSuppliedMeasurements(
  context: UnitConversionContext,
  fields: readonly ItemOverrideField[],
): UnitConversionContext {
  const { piece } = context;
  const lineSupplied = (
    field: ItemOverrideField,
    unit: DimensionUnit | WeightUnit | null | undefined,
    value: string | null | undefined,
  ) => (fields.includes(field) && unit && !value ? "1" : value);
  return {
    ...context,
    piece: {
      ...piece,
      length: lineSupplied("length", piece.dimensionUnit, piece.length),
      width: lineSupplied("width", piece.dimensionUnit, piece.width),
      weight: lineSupplied("weight", piece.weightUnit, piece.weight),
    },
  };
}

/**
 * Returns how many `to` units are in one `from` unit, or null when no path
 * exists. Full precision; callers round.
 *
 * Contract: the item's measurements always describe one `each`, independently
 * of its cost unit. Non-weight/length catalog units default to one `each` only
 * when the configured rates and measurements provide no conversion path.
 * Same-category conversions (lb↔kg, ft↔MLF, sq ft↔m²) use fixed ratios, not
 * the item's measurements. Cross-category conversions go through the piece
 * (weight, length, or length × width for square feet). The item's
 * conversion rates are usable in both directions. Search configured rates and
 * fixed ratios first, then allow measurements, then the catalog-to-each default.
 * Within each search, the shortest path wins and rate edges break ties.
 */
export function resolveConversionFactor(
  from: ConversionUnit,
  to: ConversionUnit,
  context: UnitConversionContext,
): Decimal | null {
  if (from === to) {
    return new Decimal(1);
  }

  const rates = rateEdges(context);
  const measured = [...rates, ...pieceEdges(context), ...FIXED_RATIO_EDGES];
  for (const edges of [
    [...rates, ...FIXED_RATIO_EDGES],
    measured,
    [...measured, ...defaultEachEdges(context.itemUnit)],
  ]) {
    const factor = searchFactor(
      to,
      edges,
      [{ unit: from, factor: new Decimal(1) }],
      new Set([from]),
    );
    if (factor) {
      return factor;
    }
  }
  return null;
}

/** Breadth-first search; the first path to reach a unit keeps it. */
function searchFactor(
  target: ConversionUnit,
  edges: ConversionEdge[],
  frontier: PathEnd[],
  visited: ReadonlySet<ConversionUnit>,
): Decimal | null {
  const hit = frontier.find((path) => path.unit === target);
  if (hit) {
    return hit.factor;
  }

  const reached = frontier.flatMap((path) =>
    edges
      .filter((edge) => edge.from === path.unit && !visited.has(edge.to))
      .map((edge) => ({
        unit: edge.to,
        factor: path.factor.times(edge.factor),
      })),
  );
  const next = reached.filter(
    (path, index) =>
      reached.findIndex((other) => other.unit === path.unit) === index,
  );
  if (next.length === 0) {
    return null;
  }

  return searchFactor(
    target,
    edges,
    next,
    new Set([...visited, ...next.map((path) => path.unit)]),
  );
}

function rateEdges({ rates }: UnitConversionContext): ConversionEdge[] {
  return withReverseEdges(
    rates.map((rate) => {
      const factor = toPositiveDecimal(rate.rate);
      return factor ? { from: rate.fromUnit, to: rate.toUnit, factor } : null;
    }),
  );
}

function pieceEdges({ piece }: UnitConversionContext): ConversionEdge[] {
  const weight = toPositiveDecimal(piece.weight);
  const length = toPositiveDecimal(piece.length);
  const width = toPositiveDecimal(piece.width);
  const { weightUnit, dimensionUnit } = piece;

  const edges: Array<ConversionEdge | null> = [
    weightUnit && weight
      ? { from: ItemUnitEnum.each, to: weightUnit, factor: weight }
      : null,
    dimensionUnit && length
      ? { from: ItemUnitEnum.each, to: dimensionUnit, factor: length }
      : null,
    dimensionUnit && length && width
      ? {
          from: ItemUnitEnum.each,
          to: AreaUnitEnum.square_feet,
          factor: length
            .times(FEET_PER_LENGTH_UNIT[dimensionUnit])
            .times(width.times(FEET_PER_LENGTH_UNIT[dimensionUnit])),
        }
      : null,
  ];

  return withReverseEdges(edges);
}

function withAllowance(
  value: string | null | undefined,
  allowance: Decimal | null,
): string | null | undefined {
  const size = toPositiveDecimal(value);
  return size && allowance ? size.plus(allowance).toString() : value;
}

function withReverseEdges(
  edges: Array<ConversionEdge | null>,
): ConversionEdge[] {
  return edges.flatMap((edge) =>
    edge
      ? [
          edge,
          {
            from: edge.to,
            to: edge.from,
            factor: new Decimal(1).div(edge.factor),
          },
        ]
      : [],
  );
}

function defaultEachEdges(itemUnit: ItemUnit): ConversionEdge[] {
  const isMeasureUnit = [
    ...WeightUnitSchema.options,
    ...DimensionUnitSchema.options,
    ItemUnitEnum.tons,
  ].some((unit) => unit === itemUnit);
  if (isMeasureUnit || itemUnit === ItemUnitEnum.each) {
    return [];
  }
  return withReverseEdges([
    { from: itemUnit, to: ItemUnitEnum.each, factor: new Decimal(1) },
  ]);
}

function toPositiveDecimal(value?: string | null): Decimal | null {
  if (!value) {
    return null;
  }
  try {
    const decimal = new Decimal(value);
    return decimal.isFinite() && decimal.gt(0) ? decimal : null;
  } catch {
    return null;
  }
}

function ratioEdges<U extends ConversionUnit>(
  units: readonly U[],
  basePerUnit: Record<U, Decimal>,
): ConversionEdge[] {
  return units.flatMap((from) =>
    units
      .filter((to) => to !== from)
      .map((to) => ({
        from,
        to,
        factor: basePerUnit[from].div(basePerUnit[to]),
      })),
  );
}
